import React, { useCallback, useState } from 'react';
import cn from 'classnames';
import { debounce } from 'lodash';

import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import S from 'constants/string.const';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToLowerCase from 'utils/converters/any/fromAnyToLowerCase.util';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';

import CN from './KpiAddDropdown.module.scss';

const L = logger('KpiAddDropdown'); // eslint-disable-line no-unused-vars

const SEARCH_DEBOUNCE_TIME = 300;

// TODO: move to utilities, generalize it further, add tests, if useful in other places as well
const filterItems = (options) => {
  const haystack = asArray(options?.haystack);
  const field = 'symbol' === typeof options?.field ? options?.field : fromAnyToString(options?.field);
  const needle = fromAnyToLowerCase(options?.needle);

  // TODO: should the empty string also indicate valid filtering or should it mean no filter applied? Maybe add an option for it

  return haystack.filter((item) => fromAnyToLowerCase(item?.[field]).includes(needle));
};

// TODO: move to utilities, generalize it further, add tests, if useful in other places as well
const arrayGroupBy = (key, items) => {
  items = asArray(items);
  key = typeof key === 'symbol' ? key : fromAnyToString(key);

  const result = {};

  for (const item of items) {
    const val = item?.[key];
    if (result[val]) {
      result[val].push(item);
    } else {
      result[val] = [item];
    }
  }

  return result;
};

const KpiAddDropdownItems = ({ value, onAdd }) =>
  asArray(value).map((item) => (
    <div
      data-bem="KpiAddDropdownItems__item"
      key={item?.metricKey}
      className={CN.kpiNewItem}
      onClick={() => onAdd(item)}
    >
      {item?.label}
    </div>
  ));

const KpiAddDropdownGroupHeader = ({ variant }) => (
  <div data-bem="KpiAddDropdownGroupHeader" data-variant={String(variant)} className={CN.headerGroup}>
    {'money' === variant && <Icon variant="pound" width="lg" />}
    {'money' === variant && <div className={CN.titleGroup}>Values</div>}
    {'percentages' === variant && <Icon variant="percent" width="lg" />}
    {'percentages' === variant && <div className={CN.titleGroup}>Percentages</div>}
    {'number' === variant && <Icon variant="count" width="lg" />}
    {'number' === variant && <div className={CN.titleGroup}>Number</div>}
  </div>
);

const KpiAddDropdown = ({ options, onAddKpi, height, position }) => {
  const { isDarkMode } = useTheme();
  const [query, setQuery] = useState('');

  const searchDebounced = debounce((ev) => void setQuery(fromAnyToString(ev)), SEARCH_DEBOUNCE_TIME);
  const handleSearchChange = useCallback((ev) => void searchDebounced(fromEventToValue(ev)), [searchDebounced]);

  const itemsMap = arrayGroupBy('metricType', options);
  const handleAdd = (raw) => call('KpiAddDropdown.onAddKpi', onAddKpi, { raw });

  const filteredCurs = filterItems({ field: 'label', needle: query, haystack: itemsMap.money });
  const filteredPcts = filterItems({ field: 'label', needle: query, haystack: itemsMap.percentage });
  const filteredNums = filterItems({ field: 'label', needle: query, haystack: itemsMap.number });

  const isEmpty = !isFullArray(filteredCurs) && !isFullArray(filteredPcts) && !isFullArray(filteredNums);

  return (
    <DropdownFoldout tag="KpiAddDropdown" position={position}>
      <DropdownFoldout.Body>
        <div
          data-bem="KpiAddDropdown"
          className={cn({
            [CN.component]: true,
            [CN.dark]: isDarkMode,
          })}
        >
          <div className={CN.searchContainer}>
            <input
              data-bem="KpiAddDropdown__search"
              type="text"
              className={CN.searchInput}
              placeholder="Search from 30+ KPIs"
              onChange={handleSearchChange}
            />
          </div>

          <div
            data-bem="KpiAddDropdown__group"
            className={cn({
              [CN.scrollContainer]: true,
              [CN.smallHeight]: height,
            })}
          >
            {isEmpty ? (
              <div className={CN.noResults}>{S.thinking}</div>
            ) : (
              <>
                <KpiAddDropdownGroupHeader variant="number" />
                <KpiAddDropdownItems value={filteredNums} onAdd={handleAdd} />

                <KpiAddDropdownGroupHeader variant="percentages" />
                <KpiAddDropdownItems value={filteredPcts} onAdd={handleAdd} />

                <KpiAddDropdownGroupHeader variant="money" />
                <KpiAddDropdownItems value={filteredCurs} onAdd={handleAdd} />
              </>
            )}
          </div>
        </div>
      </DropdownFoldout.Body>
    </DropdownFoldout>
  );
};

export default KpiAddDropdown;
